/**
 * useMessagePersistence
 *
 * Manages sessionStorage persistence for chat state across page navigations.
 *
 * Responsibilities:
 * - Save chat state (messages, activeConversation, userId) to sessionStorage
 * - Restore chat state on mount (with guards)
 * - Handle returnToChat navigation flows
 * - Provide manual save/clear functions
 * - Respect streaming protection windows
 *
 * Interface:
 * @param {Object} params
 * @param {string} params.userId - Current user ID
 * @param {Array} params.messages - Current messages array
 * @param {Object|null} params.activeConversation - Current active conversation
 * @param {boolean} params.isStreaming - Whether currently streaming
 * @param {boolean} params.hasProcessedMessages - Whether messages have been processed
 * @param {Object} [params.location] - React Router location object (for returnToChat)
 * @param {Function} params.onRestore - Callback when state is restored: (state) => void
 *
 * @returns {Object} { saveState, clearState }
 */

import { useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'chatState';

export function useMessagePersistence({
  userId,
  messages,
  activeConversation,
  isStreaming,
  hasProcessedMessages,
  location,
  onRestore
}) {
  // Track if we've already attempted restoration
  const hasRestoredRef = useRef(false);

  /**
   * Save current state to sessionStorage
   */
  const saveState = useCallback(({
    messages: customMessages,
    conversationId
  } = {}) => {
    const messagesToSave = customMessages || messages;
    const conversationToSave = activeConversation;

    // Only save if we have messages and an active conversation
    if (messagesToSave.length === 0 || !conversationToSave) {
      return;
    }

    const stateToSave = {
      userId,
      messages: messagesToSave,
      activeConversation: conversationToSave
    };

    // Include conversationId if provided
    if (conversationId) {
      stateToSave.conversationId = conversationId;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [userId, messages, activeConversation]);

  /**
   * Clear state from sessionStorage
   */
  const clearState = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Restore state from sessionStorage
   */
  const restoreState = useCallback((isFromReturnToChat = false) => {
    const savedState = sessionStorage.getItem(STORAGE_KEY);
    if (!savedState) return;

    try {
      const parsedState = JSON.parse(savedState);

      // Validate userId matches
      if (parsedState.userId !== userId) {
        clearState();
        return;
      }

      const { messages: savedMessages, activeConversation: savedConversation } = parsedState;

      // Build restore data
      const restoreData = {};

      // Only restore messages if they exist
      if (savedMessages && savedMessages.length > 0) {
        restoreData.messages = savedMessages;
      }

      // Always restore conversation if it exists
      if (savedConversation) {
        restoreData.activeConversation = savedConversation;
      }

      // Add extra flags for returnToChat flow
      if (isFromReturnToChat) {
        restoreData.isFirstMessage = false;
        restoreData.initialTransitionComplete = true;
      }

      // Call restore callback
      onRestore(restoreData);

    } catch (error) {
      console.error('Error parsing session storage:', error);
      clearState();
    }
  }, [userId, onRestore, clearState]);

  /**
   * Effect: Handle location.state.returnToChat restoration
   */
  useEffect(() => {
    if (location?.state?.returnToChat && !hasRestoredRef.current) {
      hasRestoredRef.current = true;
      restoreState(true);
    }
  }, [location, restoreState]);

  /**
   * Effect: Handle initial mount restoration
   * Only runs if:
   * - Messages haven't been processed yet
   * - No activeConversation prop provided
   * - Haven't already restored
   */
  useEffect(() => {
    if (
      !hasProcessedMessages &&
      !activeConversation &&
      !hasRestoredRef.current
    ) {
      hasRestoredRef.current = true;
      restoreState(false);
    }
  }, [hasProcessedMessages, activeConversation, restoreState]);

  /**
   * Effect: Auto-save on unmount
   * Only saves if we have messages and an active conversation
   * Does NOT save if streaming (protection window)
   */
  useEffect(() => {
    return () => {
      if (messages.length > 0 && activeConversation && !isStreaming) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          userId,
          messages,
          activeConversation
        }));
      }
    };
  }, [userId, messages, activeConversation, isStreaming]);

  /**
   * Effect: Save when conversation messages are loaded from Firestore
   * Guards:
   * - Only when activeConversation exists
   * - Only when messages change
   * - NOT during streaming
   */
  useEffect(() => {
    if (
      activeConversation &&
      messages.length > 0 &&
      !isStreaming
    ) {
      saveState({
        messages,
        conversationId: activeConversation.id
      });
    }
  }, [activeConversation, messages, isStreaming, saveState]);

  return {
    saveState,
    clearState
  };
}
